import type {
  AuthResponse,
  LoginCredentials,
  Pharmacy,
  RegisterData,
  RegisterEmployeeResponse,
  User,
} from '@/types'
import { apiClient } from './client'
import { clearAuthTokens, getAccessToken, setAuthTokens } from './storage'
import { getApiErrorMessage } from './errors'
import { DEFAULT_LOCALE, isSupportedLocale, t } from '@/i18n'

interface BackendUserResponse {
  id: string
  email: string
  status: 'active' | 'pending_approval' | 'rejected' | 'disabled'
  givenNames: string | null
  familyName1: string | null
  familyName2: string | null
  displayName: string | null
  phone: string | null
  language: 'en' | 'es'
  pharmacyMemberships: Array<{
    pharmacyId: string
    role: 'pharmacist' | 'owner'
  }>
  globalRoles: Array<'campaign_admin' | 'reviewer' | 'reviewer_l2'>
}

interface BackendAuthResponse {
  accessToken: string
  refreshToken: string
  expiresAt: string
  user: BackendUserResponse
}

interface BackendPharmacyResponse {
  id: string
  name: string
  taxId: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  province: string | null
  postalCode: string | null
  countryCode: string
  phone: string | null
}

function normalizePhoneDigits(value: string): string {
  return value.replace(/\D/g, '')
}

function normalizePhoneForUi(phone: string | null): string | undefined {
  if (!phone) return undefined

  let digits = normalizePhoneDigits(phone)
  if (digits.startsWith('0034')) {
    digits = digits.slice(4)
  } else if (digits.startsWith('34') && digits.length > 9) {
    digits = digits.slice(2)
  }

  if (digits.length > 9) {
    digits = digits.slice(-9)
  }

  return digits || undefined
}

function normalizePhoneForApi(phone: string): string | null {
  const digits = normalizePhoneDigits(phone)
  if (!digits) return null
  if (digits.startsWith('0034')) return digits.slice(4)
  if (digits.startsWith('34') && digits.length > 9) return digits.slice(2)
  return digits
}

function toUser(user: BackendUserResponse): User {
  const membership = user.pharmacyMemberships[0]
  const givenNames = user.givenNames ?? ''
  const family = [user.familyName1, user.familyName2].filter(Boolean).join(' ')

  return {
    id: user.id,
    email: user.email,
    name: givenNames || user.displayName || '',
    surname: family,
    nif: '',
    role: membership?.role ?? 'pharmacist',
    status: user.status,
    globalRoles: user.globalRoles ?? [],
    pharmacyId: membership?.pharmacyId ?? '',
    phone: normalizePhoneForUi(user.phone),
    language: isSupportedLocale(user.language) ? user.language : DEFAULT_LOCALE,
    createdAt: new Date(0),
    updatedAt: new Date(0),
  }
}

function toPharmacy(pharmacy: BackendPharmacyResponse): Pharmacy {
  const addressParts = [pharmacy.addressLine1, pharmacy.addressLine2].filter(Boolean)
  return {
    id: pharmacy.id,
    name: pharmacy.name,
    nif: pharmacy.taxId ?? '',
    address: addressParts.join(', '),
    city: pharmacy.city ?? '',
    postalCode: pharmacy.postalCode ?? '',
    province: pharmacy.province ?? '',
    createdAt: new Date(0),
  }
}

function splitSurname(surname: string): { familyName1?: string; familyName2?: string } {
  const parts = surname.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return {}
  if (parts.length === 1) return { familyName1: parts[0] }
  return { familyName1: parts[0], familyName2: parts.slice(1).join(' ') }
}

function buildNamePayload(data: RegisterData): {
  givenNames: string
  familyName1: string | null
  familyName2: string | null
} {
  const { familyName1, familyName2 } = splitSurname(data.surname)
  return {
    givenNames: data.name.trim(),
    familyName1: familyName1 ?? null,
    familyName2: familyName2 ?? null,
  }
}

function setTokens(response: BackendAuthResponse): void {
  setAuthTokens({
    accessToken: response.accessToken,
    refreshToken: response.refreshToken,
    expiresAt: response.expiresAt,
  })
}

function toAuthResponse(response: BackendAuthResponse): AuthResponse {
  return {
    user: toUser(response.user),
    token: response.accessToken,
    refreshToken: response.refreshToken,
    expiresAt: new Date(response.expiresAt),
  }
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<BackendAuthResponse>('/api/v1/auth/login', {
        email: credentials.email,
        password: credentials.password,
      })

      setTokens(response.data)
      return toAuthResponse(response.data)
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.login')))
    }
  },

  async registerOwner(data: RegisterData): Promise<AuthResponse> {
    if (!data.nif?.trim()) {
      throw new Error(t('errors.nifRequiredOwner'))
    }

    if (!data.activationCode?.trim()) {
      throw new Error(t('errors.activationCodeRequired'))
    }

    try {
      const namePayload = buildNamePayload(data)
      const response = await apiClient.post<BackendAuthResponse>('/api/v1/auth/register-owner', {
        email: data.email,
        password: data.password,
        nif: data.nif.trim().toUpperCase(),
        activationCode: data.activationCode.trim(),
        ...namePayload,
        phone: data.phone ?? null,
      })

      setTokens(response.data)
      return toAuthResponse(response.data)
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.registerOwner')))
    }
  },

  async registerEmployee(data: RegisterData): Promise<RegisterEmployeeResponse> {
    if (!data.nif?.trim()) {
      throw new Error(t('errors.nifRequiredEmployee'))
    }

    try {
      const namePayload = buildNamePayload(data)
      const response = await apiClient.post<RegisterEmployeeResponse>('/api/v1/auth/register-employee', {
        email: data.email,
        password: data.password,
        nif: data.nif.trim().toUpperCase(),
        ...namePayload,
        phone: data.phone ?? null,
      })

      return response.data
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.registerEmployee')))
    }
  },

  async register(data: RegisterData): Promise<AuthResponse | RegisterEmployeeResponse> {
    const role = data.role ?? 'employee'
    if (role === 'owner') {
      return this.registerOwner(data)
    }

    return this.registerEmployee(data)
  },

  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem('pg_pharma_refresh_token')
      await apiClient.post('/api/v1/auth/logout', refreshToken ? { refreshToken } : undefined)
    } finally {
      clearAuthTokens()
    }
  },

  async getCurrentUser(): Promise<User | null> {
    const token = getAccessToken()
    if (!token) return null

    try {
      const response = await apiClient.get<BackendUserResponse>('/api/v1/auth/me')
      return toUser(response.data)
    } catch {
      clearAuthTokens()
      return null
    }
  },

  getToken(): string | null {
    return getAccessToken()
  },

  async requestPasswordReset(email: string): Promise<void> {
    try {
      await apiClient.post('/api/v1/auth/password-reset', { email })
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.requestPasswordReset')))
    }
  },

  async confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
    try {
      await apiClient.post('/api/v1/auth/password-reset/confirm', {
        email,
        code,
        newPassword,
      })
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.resetPassword')))
    }
  },

  async updateProfile(
    _userId: string,
    data: Partial<Pick<User, 'name' | 'surname' | 'phone' | 'language'>>
  ): Promise<User> {
    const { familyName1, familyName2 } = data.surname ? splitSurname(data.surname) : {}

    try {
      const normalizedPhone =
        data.phone !== undefined ? normalizePhoneForApi(data.phone) : undefined

      const response = await apiClient.patch<BackendUserResponse>('/api/v1/me', {
        ...(data.name !== undefined ? { givenNames: data.name } : {}),
        ...(data.phone !== undefined ? { phone: normalizedPhone } : {}),
        ...(data.surname !== undefined ? { familyName1: familyName1 ?? null, familyName2: familyName2 ?? null } : {}),
        ...(data.language !== undefined ? { language: data.language } : {}),
      })

      return toUser(response.data)
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.updateProfile')))
    }
  },

  async getPharmacy(_pharmacyId: string): Promise<Pharmacy | null> {
    try {
      const response = await apiClient.get<BackendPharmacyResponse>('/api/v1/me/pharmacy')
      return toPharmacy(response.data)
    } catch (error) {
      throw new Error(getApiErrorMessage(error, t('errors.loadPharmacy')))
    }
  },
}
